import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, Command, ArrowRight, FileText, Building2, 
  TrendingUp, Newspaper, Shield, HelpCircle, X,
  Keyboard, Zap, Users, CreditCard, Home
} from "lucide-react";
import { API } from "@/App";

/**
 * CommandPalette - Phase 46: Global Keyboard Shortcuts
 * 
 * Shortcuts:
 * - ⌘/Ctrl + K: Open command palette
 * - ⌘/Ctrl + /: Open help
 * - Escape: Close palette
 * 
 * Quick actions from palette:
 * - Search companies/assets
 * - Navigate to pages
 * - Common actions
 */

const COMMANDS = [
  // Navigation - Phase 120: Updated to new route names
  { id: "nav-overview", label: "Go to Overview", keywords: "home dashboard command center", icon: Home, action: "navigate", path: "/overview", category: "Navigation" },
  { id: "nav-research", label: "Go to Research", keywords: "research search company asset intelligence", icon: Search, action: "navigate", path: "/research", category: "Navigation" },
  { id: "nav-markets", label: "Go to Markets", keywords: "market landscape strategy analysis arena", icon: TrendingUp, action: "navigate", path: "/markets", category: "Navigation" },
  { id: "nav-dealflow", label: "Go to Dealflow", keywords: "dealflow radar private market form d filings", icon: TrendingUp, action: "navigate", path: "/dealflow", category: "Navigation" },
  { id: "nav-monitor", label: "Go to Monitor", keywords: "wire news alerts watchlist", icon: Newspaper, action: "navigate", path: "/monitor", category: "Navigation" },
  { id: "nav-support", label: "Go to Support Hub", keywords: "help tickets", icon: HelpCircle, action: "navigate", path: "/support", category: "Navigation" },
  
  // Actions
  { id: "action-search", label: "Search Company or Asset", keywords: "find lookup query", icon: Search, action: "focus-search", category: "Actions" },
  { id: "action-compare", label: "Open Compare Panel", keywords: "side by side", icon: Building2, action: "open-compare", category: "Actions" },
  { id: "action-help", label: "Open Help Guide", keywords: "documentation faq", icon: HelpCircle, action: "open-help", category: "Actions" },
  { id: "action-shortcuts", label: "View Keyboard Shortcuts", keywords: "hotkeys commands", icon: Keyboard, action: "show-shortcuts", category: "Actions" },
  
  // Admin (only shown to admins)
  { id: "admin-dashboard", label: "Admin Dashboard", keywords: "god mode users", icon: Shield, action: "navigate", path: "/admin", category: "Admin", adminOnly: true },
  { id: "admin-users", label: "Manage Users", keywords: "team members", icon: Users, action: "navigate", path: "/admin?tab=users", category: "Admin", adminOnly: true },
  { id: "admin-subscriptions", label: "Manage Subscriptions", keywords: "billing payments", icon: CreditCard, action: "navigate", path: "/admin?tab=orgs", category: "Admin", adminOnly: true },
];

// Phase 120: Updated keyboard shortcuts with new navigation labels
const KEYBOARD_SHORTCUTS = [
  { keys: ["⌘", "K"], description: "Open command palette", windows: ["Ctrl", "K"] },
  { keys: ["⌘", "/"], description: "Open help guide", windows: ["Ctrl", "/"] },
  { keys: ["⌘", "Enter"], description: "Submit search", windows: ["Ctrl", "Enter"] },
  { keys: ["Escape"], description: "Close modal/palette", windows: ["Escape"] },
  { keys: ["⌘", "1"], description: "Go to Overview", windows: ["Ctrl", "1"] },
  { keys: ["⌘", "2"], description: "Go to Research", windows: ["Ctrl", "2"] },
  { keys: ["⌘", "3"], description: "Go to Markets", windows: ["Ctrl", "3"] },
  { keys: ["⌘", "4"], description: "Go to Monitor", windows: ["Ctrl", "4"] },
];

export function useKeyboardShortcuts(options = {}) {
  const { onOpenPalette, onOpenHelp, onFocusSearch } = options;
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;
      
      // ⌘/Ctrl + K: Open command palette
      if (modifier && e.key === 'k') {
        e.preventDefault();
        onOpenPalette?.();
      }
      
      // ⌘/Ctrl + /: Open help
      if (modifier && e.key === '/') {
        e.preventDefault();
        onOpenHelp?.();
      }
      
      // ⌘/Ctrl + 1-4: Quick navigation
      if (modifier && ['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault();
        const routes = ['/overview', '/research', '/markets', '/monitor'];
        const idx = parseInt(e.key) - 1;
        if (routes[idx]) navigate(routes[idx]);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, onOpenPalette, onOpenHelp, onFocusSearch]);
}

export function CommandPalette({ isOpen, onClose, isAdmin = false }) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  
  // Filter commands based on query and admin status
  const filteredCommands = COMMANDS.filter(cmd => {
    if (cmd.adminOnly && !isAdmin) return false;
    if (!query) return true;
    const searchStr = `${cmd.label} ${cmd.keywords}`.toLowerCase();
    return searchStr.includes(query.toLowerCase());
  });
  
  // Group by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});
  
  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery("");
      setSelectedIndex(0);
      setShowShortcuts(false);
    }
  }, [isOpen]);
  
  const executeCommand = useCallback((cmd) => {
    switch (cmd.action) {
      case 'navigate':
        navigate(cmd.path);
        onClose();
        break;
      case 'focus-search':
        onClose();
        setTimeout(() => {
          const searchInput = document.querySelector('[data-testid="search-input"]');
          searchInput?.focus();
        }, 100);
        break;
      case 'open-help':
        onClose();
        setTimeout(() => {
          const helpBtn = document.querySelector('[title="Help & Guide"]');
          helpBtn?.click();
        }, 100);
        break;
      case 'open-compare':
        onClose();
        setTimeout(() => {
          const compareBtn = document.querySelector('[data-testid="compare-dock"]');
          compareBtn?.click();
        }, 100);
        break;
      case 'show-shortcuts':
        setShowShortcuts(true);
        break;
      default:
        onClose();
    }
  }, [navigate, onClose]);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (showShortcuts) {
        setShowShortcuts(false);
      } else {
        onClose();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
      executeCommand(filteredCommands[selectedIndex]);
    }
  }, [filteredCommands, selectedIndex, showShortcuts, onClose, executeCommand]);
  
  if (!isOpen) return null;
  
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Palette */}
      <div 
        className="relative w-full max-w-xl bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {showShortcuts ? (
          /* Shortcuts View */
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Keyboard size={14} className="text-emerald-600" />
                Keyboard Shortcuts
              </h3>
              <button 
                onClick={() => setShowShortcuts(false)}
                className="text-slate-400 hover:text-slate-900"
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="space-y-2">
              {KEYBOARD_SHORTCUTS.map((shortcut, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-700">{shortcut.description}</span>
                  <div className="flex items-center gap-1">
                    {(isMac ? shortcut.keys : shortcut.windows).map((key, i) => (
                      <span key={i}>
                        <kbd className="px-2 py-1 bg-slate-100 border border-slate-200 rounded-sm text-[11px] font-mono text-slate-700">
                          {key}
                        </kbd>
                        {i < (isMac ? shortcut.keys : shortcut.windows).length - 1 && (
                          <span className="text-slate-400 mx-1">+</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-slate-500 mt-4 text-center">
              Press <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-[10px]">Esc</kbd> to go back
            </p>
          </div>
        ) : (
          /* Command Search View */
          <>
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
              <Command size={16} className="text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                placeholder="Type a command or search..."
                className="flex-1 bg-transparent text-slate-900 text-sm placeholder-slate-400 outline-none"
                data-testid="command-palette-input"
              />
              <kbd className="px-2 py-1 bg-slate-100 border border-slate-200 rounded-sm font-mono text-[11px] text-slate-500">
                esc
              </kbd>
            </div>
            
            {/* Commands List */}
            <div className="max-h-[300px] overflow-y-auto py-2">
              {Object.entries(groupedCommands).map(([category, commands]) => (
                <div key={category}>
                  <div className="px-4 py-1">
                    <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest font-medium">
                      {category}
                    </span>
                  </div>
                  {commands.map((cmd, idx) => {
                    const globalIdx = filteredCommands.indexOf(cmd);
                    const isSelected = globalIdx === selectedIndex;
                    const Icon = cmd.icon;
                    
                    return (
                      <button
                        key={cmd.id}
                        onClick={() => executeCommand(cmd)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                          isSelected ? "bg-emerald-50 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                        }`}
                        data-testid={`cmd-${cmd.id}`}
                      >
                        <Icon size={16} className={isSelected ? "text-emerald-600" : "text-slate-400"} />
                        <span className="flex-1 text-sm">{cmd.label}</span>
                        {isSelected && <ArrowRight size={14} className="text-emerald-600" />}
                      </button>
                    );
                  })}
                </div>
              ))}
              
              {filteredCommands.length === 0 && (
                <div className="px-4 py-8 text-center text-slate-500 text-sm">
                  No commands found for &quot;{query}&quot;
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-[10px] normal-case tracking-normal">↑↓</kbd>
                  navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-[10px] normal-case tracking-normal">↵</kbd>
                  select
                </span>
              </div>
              <button 
                onClick={() => setShowShortcuts(true)}
                className="flex items-center gap-1 text-slate-500 hover:text-slate-900 normal-case tracking-normal"
              >
                <Keyboard size={12} />
                View all shortcuts
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { KEYBOARD_SHORTCUTS };
export default CommandPalette;
